import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  Inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { AsyncPipe, DatePipe } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import {
  MatDialog,
  MatDialogModule,
  MatDialogRef,
  MAT_DIALOG_DATA,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { FormsModule } from '@angular/forms';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatChipsModule } from '@angular/material/chips';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDatepickerModule } from '@angular/material/datepicker';
import {
  MatNativeDateModule,
  DateAdapter,
  MAT_DATE_LOCALE,
  MAT_DATE_FORMATS,
  MAT_NATIVE_DATE_FORMATS,
  NativeDateAdapter,
} from '@angular/material/core';
import { MatDividerModule } from '@angular/material/divider';
import { ComponentDataSourceService } from '../../../services/component-data-source/component-data-source.service';
import { UserService } from '../../../services/user/user.service';
import { HttpClient } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import {
  Observable,
  BehaviorSubject,
  Subscription,
  of,
  combineLatest,
} from 'rxjs';
import {
  map,
  catchError,
  switchMap,
  tap,
  distinctUntilChanged,
} from 'rxjs/operators';
import { environment } from '../../../environment/environment';
import { LoadDataParams } from '../../../interfaces/DataSource/DataSourceParams.interface';

interface ManualCompletedModule {
  module_id: number;
  module_name: string;
  maintenance_minutes_credited: number;
}

interface ManualMinutesEntry {
  id: number;
  user_id: number;
  user_name: string;
  path_id: number;
  path_name: string;
  year: number;
  maintenance_minutes: number;
  module_minutes_total?: number;
  total_credited_minutes?: number;
  completed_modules?: ManualCompletedModule[];
  source: 'manual' | 'catch_up_event' | 'external_event' | 'other';
  source_id: number | null;
  acquire_all_needed?: boolean;
  /** Informational date (YYYY-MM-DD) for when the user acquired the minutes/modules. */
  acquired_date?: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
  created_by: number | null;
  created_by_name: string | null;
}

interface AvailableMaintenanceModule {
  module_id: number;
  module_name: string;
  maintenance_minutes: number;
  required: boolean;
  already_completed_via_event: boolean;
}

interface GroupedData {
  year: number;
  paths: {
    path_id: number;
    path_name: string;
    entries: ManualMinutesEntry[];
    total_minutes: number;
  }[];
}

interface UserData {
  id: number;
  uuid: string;
}

/** A user on the current location that can be selected in the bulk dialog. */
interface LocationUserOption {
  id: number;
  uuid: string;
  name: string;
  pathIds: number[];
}

interface BulkResultItem {
  user_id: number;
  user_name: string;
  id?: number;
  reason?: string;
  message?: string;
}

interface BulkResult {
  created: BulkResultItem[];
  skipped: BulkResultItem[];
  failed: BulkResultItem[];
}

/** Danish date formatting for the date pickers used in the dialogs below. */
const DANISH_DATE_PROVIDERS = [
  { provide: MAT_DATE_LOCALE, useValue: 'da-DK' },
  {
    provide: DateAdapter,
    useClass: NativeDateAdapter,
    deps: [MAT_DATE_LOCALE],
  },
  { provide: MAT_DATE_FORMATS, useValue: MAT_NATIVE_DATE_FORMATS },
];

/** Formats a Date as YYYY-MM-DD using the local calendar date (no timezone shift). */
function toIsoDate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Parses YYYY-MM-DD (optionally followed by a time part) into a local Date. */
function parseIsoDate(value: string | null | undefined): Date | null {
  if (!value) {
    return null;
  }
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!match) {
    return null;
  }
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
}

/** Extracts a readable message from an HttpErrorResponse produced by the DUNA API. */
function extractApiErrorMessage(error: any, fallback: string): string {
  const body = error?.error;
  if (body !== null && body !== undefined) {
    if (typeof body === 'string') {
      try {
        const parsed = JSON.parse(body);
        return parsed.message || body;
      } catch {
        return body;
      }
    }
    if (typeof body === 'object') {
      return body.message || body.error || fallback;
    }
  }
  return fallback;
}

/** Extracts the location GUID from a URL such as /lokation/<guid>. */
function extractLocationGuid(url: string): string | null {
  const match = url.match(/\/lokation\/([a-f0-9-]{36})/i);
  return match ? match[1] : null;
}

@Component({
  selector: 'app-duna-manual-maintenance-minutes',
  standalone: true,
  imports: [
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatSnackBarModule,
    FormsModule,
    MatTooltipModule,
    MatChipsModule,
    AsyncPipe,
    DatePipe,
  ],
  templateUrl: './duna-manual-maintenance-minutes.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './duna-manual-maintenance-minutes.component.scss',
})
export class DunaManualMaintenanceMinutesComponent
  implements OnInit, OnDestroy
{
  data$: Observable<any> = of({ items: [], totalCount: 0 });
  groupedData$ = new BehaviorSubject<GroupedData[]>([]);
  loading$ = new BehaviorSubject<boolean>(true);
  error$ = new BehaviorSubject<string | null>(null);

  /** 'profile' = single user (/profile/<uuid>), 'location' = bulk registration (/lokation/<guid>). */
  mode: 'profile' | 'location' = 'profile';
  locationGuid: string | null = null;

  userUuid: string | null = null;
  userId: number | null = null;
  canEdit$ = new BehaviorSubject<boolean>(false);
  userPaths$ = new BehaviorSubject<
    Array<{ path_id: number; path_name: string }>
  >([]);

  // Computed observable: show component if user has data OR can edit
  shouldShow$: Observable<boolean> = combineLatest([
    this.groupedData$,
    this.canEdit$,
  ]).pipe(
    map(([data, canEdit]) => {
      // Show if user can edit OR if there's any data
      const hasData = data && data.length > 0;
      const shouldShow = canEdit || hasData;
      return shouldShow;
    }),
    tap((shouldShow) => {
    })
  );

  private subscriptions = new Subscription();

  constructor(
    private componentDataSourceService: ComponentDataSourceService,
    private userService: UserService,
    private http: HttpClient,
    private route: ActivatedRoute,
    private router: Router,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef
  ) {
  }

  ngOnInit() {
    // Location page (/lokation/<guid>): bulk registration for several users, no single profile user
    const locationGuid =
      extractLocationGuid(this.router.url) ??
      extractLocationGuid(window.location.pathname);
    if (locationGuid) {
      this.mode = 'location';
      this.locationGuid = locationGuid;
      this.loading$.next(false);
      this.checkEditPermissions();
      return;
    }

    // Get user UUID from route or current user
    this.route.params.subscribe((params) => {
      const uuidFromRoute = params['userUuid'];
      if (uuidFromRoute) {
        this.userUuid = uuidFromRoute;
        this.loadData();
      } else {
        // Try to get from URL
        const currentUrl = window.location.pathname;
        const profileMatch = currentUrl.match(/\/profile\/([a-f0-9-]{36})/);
        if (profileMatch) {
          this.userUuid = profileMatch[1];
          this.loadData();
        } else {
          // Get current user
          this.userService.getCurrentUserFromToken().subscribe((user) => {
            if (user && user.uuid) {
              this.userUuid = user.uuid;
              this.loadData();
            } else {
              console.warn(
                '[duna-manual-maintenance-minutes] No user UUID found'
              );
              this.error$.next('Kunne ikke finde bruger');
              this.loading$.next(false);
            }
          });
        }
      }
    });

    this.subscriptions.add(
      this.route.queryParamMap
        .pipe(
          map((m) => m.get('year') ?? ''),
          distinctUntilChanged()
        )
        .subscribe(() => {
          if (this.userUuid) {
            this.loadData();
          }
        })
    );

    // Check if user can edit
    this.checkEditPermissions();

    // Log visibility state
    this.shouldShow$.subscribe((shouldShow) => {
    });

    // Log grouped data changes
    this.groupedData$.subscribe((data) => {
    });

    // Log canEdit changes
    this.canEdit$.subscribe((canEdit) => {
    });
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  private checkEditPermissions() {
    // Use getCurrentUserFromToken() to get the logged-in user, not the profile user
    const userObs = this.userService.getCurrentUserFromToken();

    userObs.subscribe({
      next: (user) => {
        if (!user || !user.roles) {
          this.canEdit$.next(false);
          return;
        }

        const roles = user.roles;
        const roleNames: string[] = [];

        if (Array.isArray(roles)) {
          roles.forEach((role: any, index: number) => {
            if (typeof role === 'string') {
              roleNames.push(role);
            } else if (typeof role === 'object' && role !== null) {
              Object.keys(role).forEach((key) => {
                const value = role[key];
                // If value is true, 'true', or a non-empty array, the role is active
                if (
                  value === true ||
                  value === 'true' ||
                  (Array.isArray(value) && value.length > 0)
                ) {
                  roleNames.push(key);
                }
              });
            }
          });
        } else if (typeof roles === 'object' && roles !== null) {
          Object.keys(roles).forEach((key) => {
            const value = (roles as any)[key];
            // If value is true, 'true', or a non-empty array, the role is active
            if (
              value === true ||
              value === 'true' ||
              (Array.isArray(value) && value.length > 0)
            ) {
              roleNames.push(key);
            }
          });
        }

        const allowedRoles = [
          'superadmin',
          'orgadmin',
          'orgbruger',
          'uddansvarlig',
        ];
        const hasPermission = allowedRoles.some((role) =>
          roleNames.includes(role)
        );
        this.canEdit$.next(hasPermission);
      },
      error: (error) => {
        console.error(
          '[duna-manual-maintenance-minutes] Error getting current user for permissions:',
          error
        );
        this.canEdit$.next(false);
      },
      complete: () => {
      },
    });
  }

  /** Matcher profil-komponenten: `?year=` eller browsers indeværende år (ikke server-default). */
  private getEffectiveProfileYearForApi(): number {
    const raw = this.route.snapshot.queryParamMap.get('year');
    if (raw != null && raw !== '') {
      const y = parseInt(raw, 10);
      if (!Number.isNaN(y)) return y;
    }
    return new Date().getFullYear();
  }

  private loadData() {
    if (!this.userUuid) {
      console.warn(
        '[duna-manual-maintenance-minutes] No userUuid, cannot load data'
      );
      this.error$.next('Ingen bruger UUID fundet');
      this.loading$.next(false);
      return;
    }

    this.loading$.next(true);
    this.error$.next(null);

    // Use the data source service
    const dataSource = this.componentDataSourceService.getDataSource(
      'profile_manual_maintenance_minutes'
    );
    if (!dataSource) {
      console.error('[duna-manual-maintenance-minutes] Data source not found');
      this.error$.next('Data source ikke fundet');
      this.loading$.next(false);
      return;
    }

    // Execute the data source with user UUID in params
    const params: LoadDataParams = {
      dataSourceKey: 'profile_manual_maintenance_minutes',
      queryParams: {
        userUuid: this.userUuid,
        year: this.getEffectiveProfileYearForApi(),
      },
    };

    dataSource
      .execute(params)
      .pipe(
        map((response: any) => {
          // Extract user_id from response if available
          if (response.user_id) {
            this.userId = response.user_id;
          }

          // Extract and store user paths if available
          if (response.user_paths && Array.isArray(response.user_paths)) {
            this.userPaths$.next(response.user_paths);
          }

          // Transform data source response to GroupedData[] format
          // Data source returns: { items: [{ year, path_id, path_name, entries, total_minutes }], ... }
          // Component expects: GroupedData[] = [{ year, paths: [...] }]

          if (
            !response.items ||
            !Array.isArray(response.items) ||
            response.items.length === 0
          ) {
            return [];
          }

          // Group items by year
          const groupedByYear: { [year: number]: any[] } = {};
          response.items.forEach((item: any) => {
            const year = item.year;
            if (!groupedByYear[year]) {
              groupedByYear[year] = [];
            }
            groupedByYear[year].push(item);
          });

          // Convert to GroupedData[] format
          const groupedArray: GroupedData[] = [];
          Object.keys(groupedByYear)
            .sort((a, b) => parseInt(b) - parseInt(a))
            .forEach((yearStr) => {
              const year = parseInt(yearStr);
              const yearItems = groupedByYear[year];

              const paths: GroupedData['paths'] = yearItems.map(
                (item: any) => ({
                  path_id: item.path_id,
                  path_name: item.path_name,
                  entries: item.entries || [],
                  total_minutes: item.total_minutes || 0,
                })
              );

              groupedArray.push({ year, paths });
            });

          return groupedArray;
        }),
        catchError((error) => {
          console.error(
            '[duna-manual-maintenance-minutes] Error loading manual maintenance minutes:',
            error
          );
          this.error$.next('Kunne ikke indlæse data');
          return of([]);
        })
      )
      .subscribe({
        next: (data) => {
          this.groupedData$.next(data);
          this.loading$.next(false);
          this.cdr.markForCheck();
        },
        error: (error) => {
          console.error(
            '[duna-manual-maintenance-minutes] Error in loadData subscription:',
            error
          );
          this.error$.next('Der opstod en fejl ved indlæsning af data');
          this.loading$.next(false);
          this.cdr.markForCheck();
        },
      });
  }

  getSourceDisplay(source: string): string {
    const sourceMap: { [key: string]: string } = {
      manual: 'Manuel',
      catch_up_event: 'Indhentningsbegivenhed',
      external_event: 'Ekstern begivenhed',
      other: 'Andet',
    };
    return sourceMap[source] || source;
  }

  getSourceColor(source: string): string {
    const colorMap: { [key: string]: string } = {
      manual: 'primary',
      catch_up_event: 'accent',
      external_event: 'warn',
      other: '',
    };
    return colorMap[source] || '';
  }

  addEntry(year: number, pathId: number, pathName: string) {
    if (!this.userId || !this.userUuid) {
      this.snackBar.open(
        'Kunne ikke tilføje post - bruger ikke fundet',
        'Luk',
        { duration: 3000 }
      );
      return;
    }

    const userPaths = this.userPaths$.value;
    const dialogRef = this.dialog.open(ManualMinutesDialogComponent, {
      width: '600px',
      data: {
        mode: 'add',
        year,
        pathId,
        pathName,
        userId: this.userId,
        userUuid: this.userUuid,
        availablePaths: userPaths || [],
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result === 'success') {
        this.loadData();
        this.snackBar.open(
          'Manuelle vedligeholdelsesminutter tilføjet. Det kan tage op til 5 minutter før ændringen træder i kraft.',
          'Luk',
          { duration: 5000 }
        );
      }
    });
  }

  addEntryFromEmptyState() {
    if (!this.userId || !this.userUuid) {
      this.snackBar.open(
        'Kunne ikke tilføje post - bruger ikke fundet',
        'Luk',
        { duration: 3000 }
      );
      return;
    }

    const userPaths = this.userPaths$.value;
    if (!userPaths || userPaths.length === 0) {
      this.snackBar.open('Ingen uddannelsesveje fundet for brugeren', 'Luk', {
        duration: 3000,
      });
      return;
    }

    const profileYear = this.getEffectiveProfileYearForApi();

    // Open dialog with path selection
    const dialogRef = this.dialog.open(ManualMinutesDialogComponent, {
      width: '600px',
      data: {
        mode: 'add',
        year: profileYear,
        pathId: userPaths.length === 1 ? userPaths[0].path_id : null,
        pathName: userPaths.length === 1 ? userPaths[0].path_name : null,
        userId: this.userId,
        userUuid: this.userUuid,
        availablePaths: userPaths,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result === 'success') {
        this.loadData();
        this.snackBar.open(
          'Manuelle vedligeholdelsesminutter tilføjet. Det kan tage op til 5 minutter før ændringen træder i kraft.',
          'Luk',
          { duration: 5000 }
        );
      }
    });
  }

  editEntry(entry: ManualMinutesEntry) {
    if (!this.userId || !this.userUuid) {
      this.snackBar.open(
        'Kunne ikke redigere post - bruger ikke fundet',
        'Luk',
        { duration: 3000 }
      );
      return;
    }

    const dialogRef = this.dialog.open(ManualMinutesDialogComponent, {
      width: '600px',
      data: {
        mode: 'edit',
        entry,
        userId: this.userId,
        userUuid: this.userUuid,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result === 'success') {
        this.loadData();
        this.snackBar.open(
          'Manuelle vedligeholdelsesminutter opdateret. Det kan tage op til 5 minutter før ændringen træder i kraft.',
          'Luk',
          { duration: 5000 }
        );
      }
    });
  }

  getEntryTotalMinutes(entry: ManualMinutesEntry): number {
    if (entry.acquire_all_needed) {
      return 0;
    }
    if (entry.total_credited_minutes != null) {
      return entry.total_credited_minutes;
    }
    const moduleTotal =
      entry.module_minutes_total ??
      entry.completed_modules?.reduce(
        (sum, m) => sum + (m.maintenance_minutes_credited || 0),
        0
      ) ??
      0;
    return (entry.maintenance_minutes || 0) + moduleTotal;
  }

  deleteEntry(entry: ManualMinutesEntry) {
    const total = this.getEntryTotalMinutes(entry);
    const confirmText = entry.acquire_all_needed
      ? 'Er du sikker på, at du vil slette denne post (opnået alt nødvendigt)?'
      : `Er du sikker på, at du vil slette denne post (${total} minutter i alt)?`;
    if (!confirm(confirmText)) {
      return;
    }

    if (!this.userId || !this.userUuid) {
      this.snackBar.open('Kunne ikke slette post - bruger ikke fundet', 'Luk', {
        duration: 3000,
      });
      return;
    }

    this.http
      .delete(
        `${environment.apiUrl}/paths/${entry.path_id}/users/${this.userId}/manual-minutes/${entry.id}`
      )
      .subscribe({
        next: () => {
          this.loadData();
          this.snackBar.open('Post slettet', 'Luk', { duration: 3000 });
        },
        error: (error) => {
          console.error('Error deleting entry:', error);
          this.snackBar.open('Kunne ikke slette post', 'Luk', {
            duration: 3000,
          });
        },
      });
  }

  refresh() {
    this.loadData();
  }

  /** Tilføj poster for det år profilen viser — også når der allerede findes poster for andre år. */
  addEntryForProfileYear(): void {
    this.addEntryFromEmptyState();
  }

  /** Converts an API date (YYYY-MM-DD) to a local Date for the date pipe. */
  getAcquiredDate(value: string | null | undefined): Date | null {
    return parseIsoDate(value);
  }

  /** Opens the bulk dialog on a location page. */
  openBulkDialog(): void {
    if (!this.locationGuid) {
      this.snackBar.open('Kunne ikke finde lokationen', 'Luk', {
        duration: 3000,
      });
      return;
    }

    const dialogRef = this.dialog.open(ManualMinutesBulkDialogComponent, {
      width: '760px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      data: {
        locationGuid: this.locationGuid,
        year: this.getEffectiveProfileYearForApi(),
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result === 'success') {
        this.snackBar.open(
          'Manuelle vedligeholdelsesminutter registreret. Det kan tage op til 5 minutter før ændringen træder i kraft.',
          'Luk',
          { duration: 5000 }
        );
      }
    });
  }
}

// Dialog component for adding/editing manual minutes
@Component({
  selector: 'app-manual-minutes-dialog',
  standalone: true,
  imports: [
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatCheckboxModule,
    MatProgressSpinnerModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatIconModule,
    FormsModule,
  ],
  providers: [...DANISH_DATE_PROVIDERS],
  template: `
    <h2 mat-dialog-title>
      {{ data.mode === 'add' ? 'Tilføj' : 'Rediger' }} manuelle
      vedligeholdelsesminutter
    </h2>
    <mat-dialog-content>
      <form #form="ngForm">
        <mat-form-field appearance="outline" class="full-width">
          <mat-label>År</mat-label>
          <input
            matInput
            type="number"
            [(ngModel)]="year"
            name="year"
            required
            [readonly]="data.mode === 'edit'"
            (ngModelChange)="onYearOrPathChange()"
          />
          @if (data.mode === 'add') {
          <mat-hint
            >Vælg år først — modullisten filtreres efter aktive moduler for
            året.</mat-hint
          >
          }
        </mat-form-field>

        @if (data.mode === 'add' && availablePaths.length > 1) {
        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Uddannelsesvej*</mat-label>
          <mat-select
            [(ngModel)]="pathId"
            name="pathId"
            required
            (selectionChange)="onPathChange()"
          >
            @for (path of availablePaths; track path) {
            <mat-option [value]="path.path_id">
              {{ path.path_name }}
            </mat-option>
            }
          </mat-select>
        </mat-form-field>
        } @if (data.mode === 'edit' || (data.mode === 'add' &&
        availablePaths.length <= 1)) {
        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Uddannelsesvej</mat-label>
          <input matInput [(ngModel)]="pathName" name="pathName" readonly />
        </mat-form-field>
        }

        <div class="full-width">
          <mat-checkbox
            [checked]="acquireAllNeeded"
            (change)="onAcquireAllNeededChange($event)"
          >
            Opnå alt nødvendigt
          </mat-checkbox>
          @if (acquireAllNeeded) {
          <p class="hint-text">
            Når denne er markeret, vil brugeren automatisk få alle nødvendige
            moduler og vedligeholdelsesminutter for dette år.
          </p>
          }
        </div>

        @if (!acquireAllNeeded) {
        <div class="module-section full-width">
          <h3 class="section-title">Moduler (aktive for {{ year }})</h3>
          @if (loadingModules) {
          <div class="module-loading">
            <mat-spinner diameter="24"></mat-spinner>
            <span>Indlæser moduler...</span>
          </div>
          } @if (!loadingModules && modulesLoadError) {
          <p class="hint-text error-text">{{ modulesLoadError }}</p>
          } @if (!loadingModules && !modulesLoadError && availableModules.length
          === 0) {
          <p class="hint-text">
            Ingen aktive vedligeholdelsesmoduler for det valgte år.
          </p>
          } @if (!loadingModules && availableModules.length > 0) {
          <div class="module-list">
            @for (mod of availableModules; track mod) {
            <mat-checkbox
              [checked]="isModuleSelected(mod.module_id)"
              (change)="onModuleToggle(mod.module_id, $event.checked)"
              class="module-checkbox"
            >
              <span class="module-label">
                {{ mod.module_name }}
                <span class="module-meta"
                  >({{ mod.maintenance_minutes }} min.)</span
                >
                @if (mod.already_completed_via_event) {
                <span class="module-meta completed-badge"
                  >Gennemført via event</span
                >
                }
              </span>
            </mat-checkbox>
            }
          </div>
          } @if (selectedModuleMinutes > 0) {
          <p class="hint-text">
            Valgte moduler giver {{ selectedModuleMinutes }} minutter i alt.
          </p>
          }
        </div>
        } @if (!acquireAllNeeded) {
        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Ekstra minutter (valgfrit)</mat-label>
          <input
            matInput
            type="number"
            [(ngModel)]="maintenanceMinutes"
            name="maintenanceMinutes"
            min="0"
            [disabled]="acquireAllNeeded"
          />
          <mat-hint>Tilføj ekstra minutter oveni de valgte moduler.</mat-hint>
        </mat-form-field>
        } @if (data.mode === 'edit' && !acquireAllNeeded && source !== 'manual')
        {
        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Kilde</mat-label>
          <mat-select [(ngModel)]="source" name="source" required>
            <mat-option value="manual">Manuel</mat-option>
            <mat-option value="catch_up_event"
              >Indhentningsbegivenhed</mat-option
            >
            <mat-option value="external_event">Ekstern begivenhed</mat-option>
            <mat-option value="other">Andet</mat-option>
          </mat-select>
        </mat-form-field>
        } @if (data.mode === 'edit' && !acquireAllNeeded && source !== 'manual')
        {
        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Kilde ID (valgfrit)</mat-label>
          <input
            matInput
            type="number"
            [(ngModel)]="sourceId"
            name="sourceId"
            placeholder="f.eks. event_id"
          />
        </mat-form-field>
        }

        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Dato opnået (valgfri)</mat-label>
          <input
            matInput
            [matDatepicker]="acquiredPicker"
            [(ngModel)]="acquiredDate"
            name="acquiredDate"
            [max]="today"
            readonly
            (click)="acquiredPicker.open()"
          />
          @if (acquiredDate) {
          <button
            mat-icon-button
            matSuffix
            type="button"
            aria-label="Ryd dato"
            (click)="acquiredDate = null"
          >
            <mat-icon>close</mat-icon>
          </button>
          }
          <mat-datepicker-toggle
            matSuffix
            [for]="acquiredPicker"
          ></mat-datepicker-toggle>
          <mat-datepicker #acquiredPicker></mat-datepicker>
          <mat-hint
            >Hvornår brugeren har opnået minutter/moduler. Påvirker ikke
            beregningen — det gør året.</mat-hint
          >
        </mat-form-field>
        @if (acquiredDateYearMismatch) {
        <p class="hint-text warn-text">
          Bemærk: datoens år afviger fra det valgte år ({{ year }}).
        </p>
        }

        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Noter*</mat-label>
          <textarea
            matInput
            [(ngModel)]="notes"
            name="notes"
            required
            rows="4"
            placeholder="Beskriv hvorfor disse minutter tilføjes..."
          ></textarea>
          <mat-hint
            >Noter er påkrævet for at dokumentere hvorfor minutterne
            tilføjes</mat-hint
          >
        </mat-form-field>
      </form>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button (click)="cancel()">Annuller</button>
      <button
        mat-raised-button
        color="primary"
        (click)="save()"
        [disabled]="!canSave(form) || saving"
      >
        @if (saving) {
        <span>Gemmer...</span>
        } @if (!saving) {
        <span>Gem</span>
        }
      </button>
    </mat-dialog-actions>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      .full-width {
        width: 100%;
        margin-bottom: 16px;
      }
      mat-dialog-content {
        min-width: 500px;
      }
      .hint-text {
        font-size: 12px;
        color: #666;
        margin-top: 8px;
        margin-left: 24px;
      }
      .error-text {
        color: #c62828;
      }
      .warn-text {
        color: #ef6c00;
        margin-top: -8px;
        margin-bottom: 16px;
      }
      .section-title {
        font-size: 14px;
        font-weight: 600;
        margin: 0 0 8px;
      }
      .module-section {
        margin-bottom: 16px;
      }
      .module-list {
        display: flex;
        flex-direction: column;
        gap: 4px;
        max-height: 220px;
        overflow-y: auto;
        border: 1px solid #e0e0e0;
        border-radius: 4px;
        padding: 8px;
      }
      .module-checkbox {
        display: block;
      }
      .module-label {
        display: inline-flex;
        flex-wrap: wrap;
        gap: 6px;
        align-items: center;
      }
      .module-meta {
        font-size: 12px;
        color: #666;
      }
      .completed-badge {
        color: #2e7d32;
      }
      .module-loading {
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 13px;
        color: #666;
      }
    `,
  ],
})
export class ManualMinutesDialogComponent {
  year: number;
  pathId: number | null = null;
  pathName: string;
  maintenanceMinutes: number = 0;
  source: 'manual' | 'catch_up_event' | 'external_event' | 'other' = 'manual';
  sourceId: number | null = null;
  notes: string = '';
  acquiredDate: Date | null = null;
  readonly today = new Date();
  saving: boolean = false;
  availablePaths: Array<{ path_id: number; path_name: string }> = [];
  acquireAllNeeded: boolean = false;
  originalMaintenanceMinutes: number | null = null;
  originalSource: 'manual' | 'catch_up_event' | 'external_event' | 'other' =
    'manual';
  originalSourceId: number | null = null;
  availableModules: AvailableMaintenanceModule[] = [];
  selectedModuleIds = new Set<number>();
  loadingModules = false;
  modulesLoadError: string | null = null;

  constructor(
    public dialogRef: MatDialogRef<ManualMinutesDialogComponent>,
    private http: HttpClient,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    if (data.mode === 'edit' && data.entry) {
      this.year = data.entry.year;
      this.pathId = data.entry.path_id;
      this.pathName = data.entry.path_name;
      this.maintenanceMinutes = data.entry.maintenance_minutes;
      this.source = data.entry.source;
      this.sourceId = data.entry.source_id;
      this.notes = data.entry.notes || '';
      this.acquireAllNeeded = !!data.entry.acquire_all_needed;
      this.acquiredDate = parseIsoDate(data.entry.acquired_date);
      if (Array.isArray(data.entry.completed_modules)) {
        data.entry.completed_modules.forEach((m: ManualCompletedModule) => {
          this.selectedModuleIds.add(m.module_id);
        });
      }
    } else {
      this.year = data.year || new Date().getFullYear();
      this.pathId = data.pathId || null;
      this.pathName = data.pathName || '';
      this.availablePaths = data.availablePaths || [];
      // Always set source to "manual" when adding directly via component
      this.source = 'manual';
      // Default the acquired date to today (can be changed or cleared)
      this.acquiredDate = new Date();

      // If pathId is set, find the path name
      if (this.pathId && this.availablePaths.length > 0) {
        const selectedPath = this.availablePaths.find(
          (p) => p.path_id === this.pathId
        );
        if (selectedPath) {
          this.pathName = selectedPath.path_name;
        }
      }
    }

    // Keep originals to restore when toggling acquireAllNeeded off
    this.originalMaintenanceMinutes = this.maintenanceMinutes;
    this.originalSource = this.source;
    this.originalSourceId = this.sourceId;
    this.loadAvailableModules();
  }

  get selectedModuleMinutes(): number {
    return this.availableModules
      .filter((m) => this.selectedModuleIds.has(m.module_id))
      .reduce((sum, m) => sum + (m.maintenance_minutes || 0), 0);
  }

  /** True when the chosen acquired date lies in a different year than the registration year. */
  get acquiredDateYearMismatch(): boolean {
    return (
      !!this.acquiredDate &&
      !!this.year &&
      this.acquiredDate.getFullYear() !== Number(this.year)
    );
  }

  canSave(form: { valid?: boolean | null }): boolean {
    if (!form?.valid || !this.notes?.trim()) {
      return false;
    }
    if (this.data.mode === 'add' && !this.pathId) {
      return false;
    }
    if (this.acquireAllNeeded) {
      return true;
    }
    return (
      (this.maintenanceMinutes || 0) > 0 || this.selectedModuleIds.size > 0
    );
  }

  onPathChange() {
    if (this.pathId) {
      const selectedPath = this.availablePaths.find(
        (p) => p.path_id === this.pathId
      );
      if (selectedPath) {
        this.pathName = selectedPath.path_name;
      }
    }
    this.onYearOrPathChange();
  }

  onYearOrPathChange() {
    if (!this.acquireAllNeeded) {
      this.loadAvailableModules();
    }
  }

  loadAvailableModules() {
    const pathId = this.pathId || this.data.pathId || this.data.entry?.path_id;
    if (!pathId || !this.data.userId || !this.year) {
      this.availableModules = [];
      return;
    }

    this.loadingModules = true;
    this.modulesLoadError = null;
    const url = `${environment.apiUrl}/paths/${pathId}/users/${this.data.userId}/manual-minutes/available-modules?year=${this.year}`;
    this.http.get<AvailableMaintenanceModule[]>(url).subscribe({
      next: (modules) => {
        this.availableModules = Array.isArray(modules) ? modules : [];
        const allowed = new Set(this.availableModules.map((m) => m.module_id));
        this.selectedModuleIds.forEach((id) => {
          if (!allowed.has(id)) {
            this.selectedModuleIds.delete(id);
          }
        });
        this.loadingModules = false;
      },
      error: (err) => {
        console.error('Error loading available modules:', err);
        this.availableModules = [];
        this.modulesLoadError = 'Kunne ikke indlæse moduler for det valgte år';
        this.loadingModules = false;
      },
    });
  }

  isModuleSelected(moduleId: number): boolean {
    return this.selectedModuleIds.has(moduleId);
  }

  onModuleToggle(moduleId: number, checked: boolean) {
    if (checked) {
      this.selectedModuleIds.add(moduleId);
    } else {
      this.selectedModuleIds.delete(moduleId);
    }
  }

  onAcquireAllNeededChange(event: any) {
    this.acquireAllNeeded = event.checked;
    if (this.acquireAllNeeded) {
      this.originalMaintenanceMinutes = this.maintenanceMinutes;
      this.originalSource = this.source;
      this.originalSourceId = this.sourceId;
      this.maintenanceMinutes = 0;
      this.sourceId = null;
      this.selectedModuleIds.clear();
    } else {
      this.maintenanceMinutes =
        this.originalMaintenanceMinutes !== null
          ? this.originalMaintenanceMinutes
          : this.maintenanceMinutes;
      this.source = this.originalSource;
      this.sourceId = this.originalSourceId;
      this.loadAvailableModules();
    }
  }

  cancel() {
    this.dialogRef.close();
  }

  save() {
    if (!this.notes || this.notes.trim() === '') {
      alert('Noter er påkrævet');
      return;
    }

    // For add mode, ensure pathId is set
    if (this.data.mode === 'add' && !this.pathId) {
      alert('Vælg venligst en uddannelsesvej');
      return;
    }

    if (!this.canSave({ valid: true })) {
      alert(
        'Angiv ekstra minutter, vælg mindst ét modul, eller marker "Opnå alt nødvendigt"'
      );
      return;
    }

    this.saving = true;

    const payload: any = {
      year: this.year,
      maintenance_minutes: this.acquireAllNeeded
        ? 0
        : this.maintenanceMinutes || 0,
      source: this.source,
      notes: this.notes.trim(),
      acquired_date: this.acquiredDate ? toIsoDate(this.acquiredDate) : null,
      acquire_all_needed: this.acquireAllNeeded,
      module_ids: this.acquireAllNeeded
        ? []
        : Array.from(this.selectedModuleIds),
    };

    // Only include source_id for edit mode
    if (this.data.mode === 'edit' && this.sourceId) {
      payload.source_id = this.sourceId;
    }

    if (this.data.mode === 'add') {
      // Use selected pathId or fallback to data.pathId
      const pathIdToUse = this.pathId || this.data.pathId;
      this.http
        .post(
          `${environment.apiUrl}/paths/${pathIdToUse}/users/${this.data.userId}/manual-minutes`,
          payload
        )
        .subscribe({
          next: () => {
            this.saving = false;
            this.dialogRef.close('success');
          },
          error: (error) => {
            console.error('Error adding manual minutes:', error);
            this.saving = false;

            // Try to extract error message from various possible structures
            let errorMessage =
              'Kunne ikke tilføje manuelle vedligeholdelsesminutter';

            // The error body should be in error.error when using default observe
            // But if it's null, the response might not have been parsed as JSON
            if (error?.error !== null && error?.error !== undefined) {
              if (typeof error.error === 'string') {
                // Try to parse as JSON if it's a string
                try {
                  const parsed = JSON.parse(error.error);
                  errorMessage = parsed.message || error.error;
                } catch {
                  errorMessage = error.error;
                }
              } else if (error.error && typeof error.error === 'object') {
                // It's already an object (parsed JSON)
                if (error.error.message) {
                  errorMessage = error.error.message;
                } else if (error.error.error) {
                  errorMessage = error.error.error;
                }
              }
            } else {
              // error.error is null - response body might be empty or not JSON
              // Show a more specific message based on status code
              if (error?.status === 400) {
                errorMessage =
                  'Der findes allerede manuelle vedligeholdelsesminutter for denne kombination. Rediger eksisterende i stedet.';
              }
            }

            alert(errorMessage);
          },
        });
    } else {
      this.http
        .patch(
          `${environment.apiUrl}/paths/${this.data.entry.path_id}/users/${this.data.userId}/manual-minutes/${this.data.entry.id}`,
          payload
        )
        .subscribe({
          next: () => {
            this.saving = false;
            this.dialogRef.close('success');
          },
          error: (error) => {
            console.error('Error updating manual minutes:', error);
            this.saving = false;
            alert('Kunne ikke opdatere manuelle vedligeholdelsesminutter');
          },
        });
    }
  }
}

// Dialog component for registering manual minutes for several users on a location at once
@Component({
  selector: 'app-manual-minutes-bulk-dialog',
  standalone: true,
  imports: [
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatCheckboxModule,
    MatProgressSpinnerModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatIconModule,
    MatDividerModule,
    FormsModule,
  ],
  providers: [...DANISH_DATE_PROVIDERS],
  template: `
    <h2 mat-dialog-title>Registrer manuelle vedligeholdelsesminutter</h2>
    <mat-dialog-content>
      @if (step === 'form') {
      <form #form="ngForm">
        @if (loadingUsers) {
        <div class="module-loading">
          <mat-spinner diameter="24"></mat-spinner>
          <span>Indlæser brugere på lokationen...</span>
        </div>
        } @if (!loadingUsers && usersError) {
        <p class="hint-text error-text no-indent">{{ usersError }}</p>
        } @if (!loadingUsers && !usersError && paths.length === 0) {
        <p class="hint-text no-indent">
          Ingen brugere på lokationen har en uddannelsesvej tildelt.
        </p>
        } @if (!loadingUsers && paths.length > 0) {
        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Uddannelsesvej*</mat-label>
          <mat-select
            [(ngModel)]="pathId"
            name="pathId"
            required
            (selectionChange)="onPathChange()"
          >
            @for (path of paths; track path.path_id) {
            <mat-option [value]="path.path_id">
              {{ path.path_name }} ({{ path.user_count }}
              {{ path.user_count === 1 ? 'bruger' : 'brugere' }})
            </mat-option>
            }
          </mat-select>
        </mat-form-field>

        <div class="row-fields">
          <mat-form-field appearance="outline" class="half-width">
            <mat-label>År</mat-label>
            <input
              matInput
              type="number"
              [(ngModel)]="year"
              name="year"
              required
              (ngModelChange)="onYearChange()"
            />
          </mat-form-field>
          <mat-form-field appearance="outline" class="half-width">
            <mat-label>Dato opnået (valgfri)</mat-label>
            <input
              matInput
              [matDatepicker]="acquiredPicker"
              [(ngModel)]="acquiredDate"
              name="acquiredDate"
              [max]="today"
              readonly
              (click)="acquiredPicker.open()"
            />
            @if (acquiredDate) {
            <button
              mat-icon-button
              matSuffix
              type="button"
              aria-label="Ryd dato"
              (click)="acquiredDate = null"
            >
              <mat-icon>close</mat-icon>
            </button>
            }
            <mat-datepicker-toggle
              matSuffix
              [for]="acquiredPicker"
            ></mat-datepicker-toggle>
            <mat-datepicker #acquiredPicker></mat-datepicker>
          </mat-form-field>
        </div>
        @if (acquiredDateYearMismatch) {
        <p class="hint-text warn-text no-indent">
          Bemærk: datoens år afviger fra det valgte år ({{ year }}).
        </p>
        }

        <div class="full-width">
          <mat-checkbox
            [checked]="acquireAllNeeded"
            (change)="onAcquireAllNeededChange($event.checked)"
          >
            Opnå alt nødvendigt
          </mat-checkbox>
          @if (acquireAllNeeded) {
          <p class="hint-text">
            Brugerne får automatisk alle nødvendige moduler og
            vedligeholdelsesminutter for det valgte år.
          </p>
          }
        </div>

        @if (!acquireAllNeeded && pathId) {
        <div class="module-section full-width">
          <h3 class="section-title">Moduler (aktive for {{ year }})</h3>
          @if (loadingModules) {
          <div class="module-loading">
            <mat-spinner diameter="24"></mat-spinner>
            <span>Indlæser moduler...</span>
          </div>
          } @if (!loadingModules && modulesLoadError) {
          <p class="hint-text error-text no-indent">{{ modulesLoadError }}</p>
          } @if (!loadingModules && !modulesLoadError && availableModules.length
          === 0) {
          <p class="hint-text no-indent">
            Ingen aktive vedligeholdelsesmoduler for det valgte år.
          </p>
          } @if (!loadingModules && availableModules.length > 0) {
          <div class="module-list">
            @for (mod of availableModules; track mod.module_id) {
            <mat-checkbox
              [checked]="selectedModuleIds.has(mod.module_id)"
              (change)="onModuleToggle(mod.module_id, $event.checked)"
              class="module-checkbox"
            >
              <span class="module-label">
                {{ mod.module_name }}
                <span class="module-meta"
                  >({{ mod.maintenance_minutes }} min.)</span
                >
              </span>
            </mat-checkbox>
            }
          </div>
          } @if (selectedModuleMinutes > 0) {
          <p class="hint-text no-indent">
            Valgte moduler giver {{ selectedModuleMinutes }} minutter pr.
            bruger.
          </p>
          }
        </div>

        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Ekstra minutter (valgfrit)</mat-label>
          <input
            matInput
            type="number"
            [(ngModel)]="maintenanceMinutes"
            name="maintenanceMinutes"
            min="0"
          />
          <mat-hint>Tilføj ekstra minutter oveni de valgte moduler.</mat-hint>
        </mat-form-field>
        }

        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Noter*</mat-label>
          <textarea
            matInput
            [(ngModel)]="notes"
            name="notes"
            required
            rows="3"
            placeholder="Beskriv hvorfor disse minutter tilføjes..."
          ></textarea>
          <mat-hint
            >Noter er påkrævet for at dokumentere hvorfor minutterne
            tilføjes</mat-hint
          >
        </mat-form-field>

        @if (pathId) {
        <div class="user-section full-width">
          <h3 class="section-title">
            Brugere ({{ selectedCount }} valgt af {{ pathUsers.length }})
          </h3>
          <mat-form-field appearance="outline" class="full-width no-margin">
            <mat-label>Søg bruger</mat-label>
            <input
              matInput
              [(ngModel)]="userSearch"
              name="userSearch"
              autocomplete="off"
            />
          </mat-form-field>
          <div class="user-actions">
            <button
              mat-button
              type="button"
              (click)="selectAllVisible()"
              [disabled]="visibleUsers.length === 0"
            >
              Vælg alle{{ userSearch ? ' viste' : '' }}
            </button>
            <button
              mat-button
              type="button"
              (click)="deselectAllVisible()"
              [disabled]="visibleUsers.length === 0"
            >
              Fravælg alle{{ userSearch ? ' viste' : '' }}
            </button>
          </div>
          <div class="user-list">
            @for (user of visibleUsers; track user.id) {
            <mat-checkbox
              [checked]="selectedUserIds.has(user.id)"
              (change)="onUserToggle(user.id, $event.checked)"
              class="module-checkbox"
            >
              {{ user.name }}
            </mat-checkbox>
            } @if (visibleUsers.length === 0) {
            <p class="hint-text no-indent">Ingen brugere matcher søgningen.</p>
            }
          </div>
        </div>
        } } @if (submitError) {
        <p class="hint-text error-text no-indent">{{ submitError }}</p>
        }
      </form>
      } @if (step === 'result' && result) {
      <div class="result-section">
        <p class="result-summary">
          <strong>{{ result.created.length }}</strong> registrering(er)
          oprettet.
          @if (result.skipped.length > 0) {
          <span
            ><strong>{{ result.skipped.length }}</strong> sprunget over.</span
          >
          } @if (result.failed.length > 0) {
          <span class="error-text"
            ><strong>{{ result.failed.length }}</strong> fejlede.</span
          >
          }
        </p>
        @if (result.skipped.length > 0) {
        <mat-divider></mat-divider>
        <h3 class="section-title result-title">Sprunget over</h3>
        <ul class="result-list">
          @for (item of result.skipped; track item.user_id) {
          <li>
            <strong>{{ item.user_name }}</strong> — {{ item.message }}
          </li>
          }
        </ul>
        } @if (result.failed.length > 0) {
        <mat-divider></mat-divider>
        <h3 class="section-title result-title">Fejlede</h3>
        <ul class="result-list error-text">
          @for (item of result.failed; track item.user_id) {
          <li>
            <strong>{{ item.user_name }}</strong> — {{ item.message }}
          </li>
          }
        </ul>
        }
      </div>
      }
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      @if (step === 'form') {
      <button mat-button (click)="close()" [disabled]="saving">Annuller</button>
      <button
        mat-raised-button
        color="primary"
        (click)="save()"
        [disabled]="!canSave() || saving"
      >
        @if (saving) {
        <span>Gemmer...</span>
        } @if (!saving) {
        <span>Registrer for {{ selectedCount }} bruger(e)</span>
        }
      </button>
      } @else {
      <button mat-raised-button color="primary" (click)="close()">Luk</button>
      }
    </mat-dialog-actions>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      .full-width {
        width: 100%;
        margin-bottom: 16px;
      }
      .no-margin {
        margin-bottom: 0;
      }
      .row-fields {
        display: flex;
        gap: 16px;
        flex-wrap: wrap;
      }
      .half-width {
        flex: 1 1 200px;
        margin-bottom: 8px;
      }
      mat-dialog-content {
        min-width: 500px;
      }
      .hint-text {
        font-size: 12px;
        color: #666;
        margin-top: 8px;
        margin-left: 24px;
      }
      .no-indent {
        margin-left: 0;
      }
      .error-text {
        color: #c62828;
      }
      .warn-text {
        color: #ef6c00;
      }
      .section-title {
        font-size: 14px;
        font-weight: 600;
        margin: 0 0 8px;
      }
      .result-title {
        margin-top: 12px;
      }
      .module-section,
      .user-section {
        margin-bottom: 16px;
      }
      .module-list,
      .user-list {
        display: flex;
        flex-direction: column;
        gap: 4px;
        max-height: 220px;
        overflow-y: auto;
        border: 1px solid #e0e0e0;
        border-radius: 4px;
        padding: 8px;
      }
      .user-actions {
        display: flex;
        gap: 8px;
        margin: 4px 0 8px;
      }
      .module-checkbox {
        display: block;
      }
      .module-label {
        display: inline-flex;
        flex-wrap: wrap;
        gap: 6px;
        align-items: center;
      }
      .module-meta {
        font-size: 12px;
        color: #666;
      }
      .module-loading {
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 13px;
        color: #666;
        margin-bottom: 12px;
      }
      .result-summary {
        display: flex;
        flex-wrap: wrap;
        gap: 12px;
        margin: 0 0 12px;
      }
      .result-list {
        margin: 0 0 8px;
        padding-left: 20px;
        font-size: 13px;
        max-height: 200px;
        overflow-y: auto;
      }
      @media (max-width: 600px) {
        mat-dialog-content {
          min-width: 0;
        }
      }
    `,
  ],
})
export class ManualMinutesBulkDialogComponent implements OnInit {
  step: 'form' | 'result' = 'form';

  // Users and paths on the location
  users: LocationUserOption[] = [];
  paths: Array<{ path_id: number; path_name: string; user_count: number }> = [];
  loadingUsers = true;
  usersError: string | null = null;

  // Form state
  pathId: number | null = null;
  year: number;
  acquiredDate: Date | null = new Date();
  readonly today = new Date();
  acquireAllNeeded = false;
  maintenanceMinutes = 0;
  notes = '';
  userSearch = '';
  selectedUserIds = new Set<number>();

  // Modules for the chosen path/year
  availableModules: AvailableMaintenanceModule[] = [];
  selectedModuleIds = new Set<number>();
  loadingModules = false;
  modulesLoadError: string | null = null;

  saving = false;
  submitError: string | null = null;
  result: BulkResult | null = null;

  private pathNames = new Map<number, string>();

  constructor(
    public dialogRef: MatDialogRef<ManualMinutesBulkDialogComponent>,
    private http: HttpClient,
    private cdr: ChangeDetectorRef,
    @Inject(MAT_DIALOG_DATA) public data: { locationGuid: string; year?: number }
  ) {
    this.year = data.year || new Date().getFullYear();
  }

  ngOnInit(): void {
    this.loadLocationUsers();
  }

  /** Users that are assigned to the selected path. */
  get pathUsers(): LocationUserOption[] {
    if (!this.pathId) {
      return [];
    }
    return this.users.filter((u) => u.pathIds.includes(this.pathId as number));
  }

  /** Path users filtered by the search box. */
  get visibleUsers(): LocationUserOption[] {
    const term = this.userSearch.trim().toLowerCase();
    if (!term) {
      return this.pathUsers;
    }
    return this.pathUsers.filter((u) => u.name.toLowerCase().includes(term));
  }

  get selectedCount(): number {
    return this.pathUsers.filter((u) => this.selectedUserIds.has(u.id)).length;
  }

  get selectedModuleMinutes(): number {
    return this.availableModules
      .filter((m) => this.selectedModuleIds.has(m.module_id))
      .reduce((sum, m) => sum + (m.maintenance_minutes || 0), 0);
  }

  get acquiredDateYearMismatch(): boolean {
    return (
      !!this.acquiredDate &&
      !!this.year &&
      this.acquiredDate.getFullYear() !== Number(this.year)
    );
  }

  canSave(): boolean {
    const year = Number(this.year);
    if (!this.notes?.trim() || !this.pathId || !(year >= 2000 && year <= 2100)) {
      return false;
    }
    if (this.selectedCount === 0) {
      return false;
    }
    if (this.acquireAllNeeded) {
      return true;
    }
    return (
      (this.maintenanceMinutes || 0) > 0 || this.selectedModuleIds.size > 0
    );
  }

  private loadLocationUsers(): void {
    this.loadingUsers = true;
    this.usersError = null;
    this.users = [];
    const collected: any[] = [];
    const pageSize = 100;
    const maxPages = 50;

    const fetchPage = (page: number) => {
      this.http
        .get<any>(`${environment.apiUrl}/locations/users`, {
          params: {
            filter_location_guid: this.data.locationGuid,
            page: String(page),
            pageIndex: String(page - 1),
            pageSize: String(pageSize),
            sortBy: 'name',
            sortDir: 'asc',
          },
        })
        .subscribe({
          next: (body) => {
            const items: any[] = Array.isArray(body)
              ? body
              : Array.isArray(body?.data)
              ? body.data
              : [];
            collected.push(...items);
            const total = Number(body?.meta?.totalItems ?? 0);
            const hasMore =
              items.length >= pageSize &&
              collected.length < total &&
              page < maxPages;
            if (hasMore) {
              fetchPage(page + 1);
              return;
            }
            this.applyLocationUsers(collected);
          },
          error: (error) => {
            console.error('Error loading location users:', error);
            this.usersError = 'Kunne ikke indlæse brugere på lokationen';
            this.loadingUsers = false;
            this.cdr.markForCheck();
          },
        });
    };

    fetchPage(1);
  }

  private applyLocationUsers(rawUsers: any[]): void {
    const byId = new Map<number, LocationUserOption>();
    const pathCounts = new Map<number, number>();
    this.pathNames.clear();

    for (const raw of rawUsers) {
      const id = Number(raw?.id);
      if (!Number.isFinite(id) || id <= 0 || byId.has(id)) {
        continue;
      }
      const rawPaths = raw?.status?.educational_paths;
      const pathList: any[] = Array.isArray(rawPaths)
        ? rawPaths
        : rawPaths && typeof rawPaths === 'object'
        ? Object.values(rawPaths)
        : [];
      const pathIds: number[] = [];
      for (const p of pathList) {
        const pid = Number(p?.path_id ?? p?.pathId);
        if (!Number.isFinite(pid) || pid <= 0 || pathIds.includes(pid)) {
          continue;
        }
        pathIds.push(pid);
        pathCounts.set(pid, (pathCounts.get(pid) || 0) + 1);
        if (!this.pathNames.has(pid)) {
          this.pathNames.set(
            pid,
            String(p?.name ?? p?.full_name ?? '').trim() || `Sti ${pid}`
          );
        }
      }
      const name =
        `${raw?.first_name ?? ''} ${raw?.last_name ?? ''}`.trim() ||
        String(raw?.user ?? '') ||
        `Bruger ${id}`;
      byId.set(id, { id, uuid: raw?.uuid ?? '', name, pathIds });
    }

    this.users = Array.from(byId.values()).sort((a, b) =>
      a.name.localeCompare(b.name, 'da')
    );
    this.paths = Array.from(pathCounts.entries())
      .map(([path_id, user_count]) => ({
        path_id,
        path_name: this.pathNames.get(path_id) || `Sti ${path_id}`,
        user_count,
      }))
      .sort((a, b) => a.path_name.localeCompare(b.path_name, 'da'));

    if (this.paths.length === 1) {
      this.pathId = this.paths[0].path_id;
      this.onPathChange();
    }
    this.loadingUsers = false;
    this.cdr.markForCheck();
  }

  onPathChange(): void {
    // Only keep selected users that are assigned to the new path
    const allowed = new Set(this.pathUsers.map((u) => u.id));
    this.selectedUserIds.forEach((id) => {
      if (!allowed.has(id)) {
        this.selectedUserIds.delete(id);
      }
    });
    this.selectedModuleIds.clear();
    this.loadAvailableModules();
  }

  onYearChange(): void {
    if (!this.acquireAllNeeded) {
      this.loadAvailableModules();
    }
  }

  onAcquireAllNeededChange(checked: boolean): void {
    this.acquireAllNeeded = checked;
    if (checked) {
      this.maintenanceMinutes = 0;
      this.selectedModuleIds.clear();
    } else {
      this.loadAvailableModules();
    }
  }

  onModuleToggle(moduleId: number, checked: boolean): void {
    if (checked) {
      this.selectedModuleIds.add(moduleId);
    } else {
      this.selectedModuleIds.delete(moduleId);
    }
  }

  onUserToggle(userId: number, checked: boolean): void {
    if (checked) {
      this.selectedUserIds.add(userId);
    } else {
      this.selectedUserIds.delete(userId);
    }
  }

  selectAllVisible(): void {
    this.visibleUsers.forEach((u) => this.selectedUserIds.add(u.id));
  }

  deselectAllVisible(): void {
    this.visibleUsers.forEach((u) => this.selectedUserIds.delete(u.id));
  }

  private loadAvailableModules(): void {
    if (!this.pathId || !this.year || this.acquireAllNeeded) {
      this.availableModules = [];
      return;
    }
    this.loadingModules = true;
    this.modulesLoadError = null;
    const url = `${environment.apiUrl}/paths/${this.pathId}/manual-minutes/available-modules?year=${this.year}`;
    this.http.get<any>(url).subscribe({
      next: (body) => {
        const modules = Array.isArray(body) ? body : body?.data;
        this.availableModules = Array.isArray(modules) ? modules : [];
        const allowed = new Set(this.availableModules.map((m) => m.module_id));
        this.selectedModuleIds.forEach((id) => {
          if (!allowed.has(id)) {
            this.selectedModuleIds.delete(id);
          }
        });
        this.loadingModules = false;
        this.cdr.markForCheck();
      },
      error: (err) => {
        console.error('Error loading available modules:', err);
        this.availableModules = [];
        this.modulesLoadError = 'Kunne ikke indlæse moduler for det valgte år';
        this.loadingModules = false;
        this.cdr.markForCheck();
      },
    });
  }

  close(): void {
    const created = this.result?.created?.length ?? 0;
    this.dialogRef.close(created > 0 ? 'success' : undefined);
  }

  save(): void {
    if (!this.pathId || this.selectedCount === 0 || !this.notes.trim()) {
      return;
    }

    this.saving = true;
    this.submitError = null;

    const userIds = this.pathUsers
      .filter((u) => this.selectedUserIds.has(u.id))
      .map((u) => u.id);

    const payload = {
      user_ids: userIds,
      year: this.year,
      acquired_date: this.acquiredDate ? toIsoDate(this.acquiredDate) : null,
      maintenance_minutes: this.acquireAllNeeded
        ? 0
        : this.maintenanceMinutes || 0,
      module_ids: this.acquireAllNeeded
        ? []
        : Array.from(this.selectedModuleIds),
      acquire_all_needed: this.acquireAllNeeded,
      source: 'manual',
      notes: this.notes.trim(),
    };

    this.http
      .post<any>(
        `${environment.apiUrl}/paths/${this.pathId}/manual-minutes/bulk`,
        payload
      )
      .subscribe({
        next: (body) => {
          const res = body?.created !== undefined ? body : body?.data ?? body;
          this.result = {
            created: res?.created ?? [],
            skipped: res?.skipped ?? [],
            failed: res?.failed ?? [],
          };
          this.saving = false;
          this.step = 'result';
          this.cdr.markForCheck();
        },
        error: (error) => {
          console.error('Error adding bulk manual minutes:', error);
          this.saving = false;
          this.submitError = extractApiErrorMessage(
            error,
            'Kunne ikke registrere manuelle vedligeholdelsesminutter'
          );
          this.cdr.markForCheck();
        },
      });
  }
}
